import {useEffect, useState} from 'react';
import {DriveFile} from '../../drive/types';
import {computeHistogram, Histogram} from '../../domain/histogram';
import {ShownImage} from './ViewerStage';

/** Pictures are counted at most this large: plenty for the histogram's shape, a few ms to count. */
const MAX_SIDE = 800;
const CACHE_SIZE = 40;

/** What a photo's histogram is read from: the decoded image the viewer shows of it. */
export interface HistogramSource {
    fileId: string;
    /** The image's URL, identifying it for the cache. */
    key: string;
    image: HTMLImageElement;
}

/**
 * The image the stage shows of the photo at hand (Drive's preview, or the original once shown), so
 * the histogram needs no download of its own; null until that is decoded.
 */
export function histogramSource(file: DriveFile, shown: ShownImage | null): HistogramSource | null {
    return shown && shown.fileId === file.id ? {fileId: file.id, key: shown.url, image: shown.element} : null;
}

// Histograms by source key, most recently used last: stepping back to a photo needs no recount.
const cache = new Map<string, Histogram>();

function remember(key: string, histogram: Histogram) {
    cache.delete(key);
    cache.set(key, histogram);
    if (cache.size > CACHE_SIZE) cache.delete(cache.keys().next().value as string);
}

/**
 * Counts the image's pixels, shrunk to at most `MAX_SIDE`. Throws (a `SecurityError`) for an image
 * from another origin, e.g. Drive's preview, whose pixels the browser won't let us read; the
 * original, a blob of our own, can be read.
 */
function computeImageHistogram(image: HTMLImageElement): Histogram {
    const k = Math.min(1, MAX_SIDE / Math.max(image.naturalWidth, image.naturalHeight, 1));
    const w = Math.max(1, Math.round(image.naturalWidth * k));
    const h = Math.max(1, Math.round(image.naturalHeight * k));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', {willReadFrequently: true});
    if (!ctx) throw new Error('No 2D canvas');
    // Samples actual pixels instead of averaging them, which would hide clipped ones.
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(image, 0, 0, w, h);
    return computeHistogram(ctx.getImageData(0, 0, w, h).data);
}

export interface HistogramState {
    /** Undefined while counting (or when there is nothing to count). */
    histogram: Histogram | undefined;
    /** The image's pixels can't be read (it came from another origin, e.g. Drive's preview). */
    failed: boolean;
}

/**
 * The histogram of `source`, cached per image. While a better image of the same photo is counted
 * (the original after the preview), the earlier one's histogram stays.
 */
export function useHistogram(source: HistogramSource | null): HistogramState {
    const [result, setResult] = useState<{ fileId: string; key: string; histogram: Histogram | null }>();
    const {fileId, key, image} = source ?? {};
    const cached = key ? cache.get(key) : undefined;

    useEffect(() => {
        if (!fileId || !key || !image) return;
        let histogram = cache.get(key) ?? null;
        if (!histogram) {
            try {
                histogram = computeImageHistogram(image);
                remember(key, histogram);
            } catch {
                histogram = null;
            }
        }
        // Kept as this photo's latest, to stay up while its original is counted.
        setResult({fileId, key, histogram});
    }, [fileId, key, image]);

    if (cached) return {histogram: cached, failed: false};
    const own = result && result.fileId === fileId ? result : undefined;
    return {
        histogram: own?.histogram ?? undefined,
        failed: !!own && own.key === key && own.histogram === null,
    };
}
